import type { InstalledWebContentPackage, SignedWebContentCatalog } from "./webContentTypes";

const DATABASE_NAME = "wordinarius-echo-web";
const DATABASE_VERSION = 1;
const STORE_NAME = "content";
const CATALOG_KEY = "catalog";
const PACKAGE_PREFIX = "package:";
const memoryCache = new Map<string, unknown>();

function openDatabase(): Promise<IDBDatabase | null> {
  if (typeof indexedDB === "undefined") {
    return Promise.resolve(null);
  }
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      if (!request.result.objectStoreNames.contains(STORE_NAME)) {
        request.result.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error || new Error("content_cache_open_failed"));
  });
}

async function readValue<T>(key: string): Promise<T | null> {
  const memoryValue = memoryCache.has(key) ? memoryCache.get(key) as T : null;
  try {
    const database = await openDatabase();
    if (!database) {
      return memoryValue;
    }
    const storedValue = await new Promise<T | null>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readonly");
      const request = transaction.objectStore(STORE_NAME).get(key);
      request.onsuccess = () => resolve((request.result as T | undefined) || null);
      request.onerror = () => reject(request.error || new Error("content_cache_read_failed"));
      transaction.oncomplete = () => database.close();
    });
    if (storedValue !== null) {
      memoryCache.set(key, storedValue);
    }
    return storedValue ?? memoryValue;
  } catch {
    return memoryValue;
  }
}

async function writeValue(key: string, value: unknown): Promise<void> {
  memoryCache.set(key, value);
  try {
    const database = await openDatabase();
    if (!database) {
      return;
    }
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readwrite");
      transaction.objectStore(STORE_NAME).put(value, key);
      transaction.oncomplete = () => {
        database.close();
        resolve();
      };
      transaction.onerror = () => reject(transaction.error || new Error("content_cache_write_failed"));
    });
  } catch {
    // Managed browsers may deny IndexedDB. The in-memory copy keeps the current
    // learning session usable, while a later reload can retry persistent storage.
  }
}

async function readValuesByPrefix<T>(prefix: string): Promise<T[]> {
  const values = new Map<string, T>();
  for (const [key, value] of memoryCache.entries()) {
    if (key.startsWith(prefix)) {
      values.set(key, value as T);
    }
  }
  try {
    const database = await openDatabase();
    if (!database) return [...values.values()];
    await new Promise<void>((resolve, reject) => {
      const transaction = database.transaction(STORE_NAME, "readonly");
      const request = transaction.objectStore(STORE_NAME).openCursor();
      request.onsuccess = () => {
        const cursor = request.result;
        if (!cursor) {
          resolve();
          return;
        }
        const key = String(cursor.key);
        if (key.startsWith(prefix)) {
          values.set(key, cursor.value as T);
          memoryCache.set(key, cursor.value);
        }
        cursor.continue();
      };
      request.onerror = () => reject(request.error || new Error("content_cache_list_failed"));
      transaction.oncomplete = () => database.close();
    });
  } catch {
    // Fall through to the current-session cache when persistent storage is blocked.
  }
  return [...values.values()];
}

export function readCachedWebContentCatalog(): Promise<SignedWebContentCatalog | null> {
  return readValue<SignedWebContentCatalog>(CATALOG_KEY);
}

export function cacheWebContentCatalog(catalog: SignedWebContentCatalog): Promise<void> {
  return writeValue(CATALOG_KEY, catalog);
}

export function readInstalledWebContentPackage(packageId: string): Promise<InstalledWebContentPackage | null> {
  return readValue<InstalledWebContentPackage>(`${PACKAGE_PREFIX}${packageId}`);
}

export function cacheInstalledWebContentPackage(value: InstalledWebContentPackage): Promise<void> {
  return writeValue(`${PACKAGE_PREFIX}${value.entry.packageId}`, value);
}

export function readAllInstalledWebContentPackages(): Promise<InstalledWebContentPackage[]> {
  return readValuesByPrefix<InstalledWebContentPackage>(PACKAGE_PREFIX);
}
